import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { createServerWs, serverAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';
import Editor from '@monaco-editor/react';

function getLanguage(path: string): string {
  const ext = (path.split('.').pop() ?? '').toLowerCase();
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript',
    json: 'json', yaml: 'yaml', yml: 'yaml', php: 'php', py: 'python', go: 'go',
    sh: 'shell', bash: 'shell', conf: 'nginx', nginx: 'nginx',
    sql: 'pgsql', html: 'html', css: 'css', md: 'markdown', xml: 'xml',
  };
  return map[ext] ?? 'plaintext';
}

interface DirEntry {
  name: string;
  path: string;
  is_dir: boolean;
  size: number;
  mod_time: string;
  mode: string;
}

interface FileEdit {
  id: number;
  file_path: string;
  size_bytes: number;
  created_at: string;
}

interface LocalDraft {
  content: string;
  savedAt: string;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const FileBrowserPage: React.FC<{ standalone?: boolean; serverId?: number }> = ({ standalone = true, serverId: propServerId }) => {
  useDocumentTitle('File Browser');
  const params = useParams<{ id: string }>();
  const serverId = propServerId !== undefined ? propServerId : parseInt(params.id || '0', 10);

  const [currentPath, setCurrentPath] = useState('/var/www');
  const [entries, setEntries]         = useState<DirEntry[]>([]);
  const [wsStatus, setWsStatus]       = useState<'connecting' | 'open' | 'closed'>('connecting');
  const [error, setError]             = useState<string | null>(null);
  const [loading, setLoading]         = useState(false);

  // Editor state
  const [openFile, setOpenFile]       = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [editorDirty, setEditorDirty] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorTab, setEditorTab]     = useState<'edit' | 'history'>('edit');
  const [fileHistory, setFileHistory] = useState<FileEdit[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoringEdit, setRestoringEdit] = useState<number | null>(null);

  const wsRef    = useRef<WebSocket | null>(null);
  const pending  = useRef<Map<string, (payload: any) => void>>(new Map());
  const draftTimer = useRef<NodeJS.Timeout | null>(null);

  const draftKey = openFile ? `cloudayn_filedraft_${serverId}_${btoa(openFile)}` : null;

  const sendWs = useCallback((msg: object): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return reject(new Error('Not connected to agent'));
      }
      const requestId = Math.random().toString(36).slice(2);
      const full = { ...msg, requestId };
      const timer = setTimeout(() => {
        pending.current.delete(requestId);
        reject(new Error('Request timed out'));
      }, 30000);
      pending.current.set(requestId, (payload) => {
        clearTimeout(timer);
        resolve(payload);
      });
      wsRef.current.send(JSON.stringify(full));
    });
  }, []);

  // Connect WS
  useEffect(() => {
    const ws = createServerWs(serverId);
    if (!ws) { setWsStatus('closed'); return; }
    wsRef.current = ws;
    setWsStatus('connecting');

    ws.onopen = () => setWsStatus('open');
    ws.onclose = () => setWsStatus('closed');
    ws.onerror = () => setWsStatus('closed');

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.requestId && pending.current.has(msg.requestId)) {
          const cb = pending.current.get(msg.requestId)!;
          pending.current.delete(msg.requestId);
          cb(msg.payload ?? msg);
        }
      } catch {}
    };

    return () => ws.close();
  }, [serverId]);

  // Auto-load directory once connected
  useEffect(() => {
    if (wsStatus === 'open') listDirectory(currentPath);
  }, [wsStatus]); // eslint-disable-line

  const listDirectory = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await sendWs({ type: 'file_list', payload: { path } });
      if (result?.error) {
        setError(result.error);
        setEntries([]);
      } else {
        setEntries(result?.entries || []);
        setCurrentPath(path);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to list directory');
    } finally {
      setLoading(false);
    }
  }, [sendWs]);

  const openFileInEditor = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await sendWs({ type: 'file_read', payload: { path } });
      if (result?.error) {
        setError(result.error);
      } else {
        const content = result?.content || '';
        // Check for unsaved draft
        const draft = (() => {
          try {
            const key = `cloudayn_filedraft_${serverId}_${btoa(path)}`;
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) as LocalDraft : null;
          } catch { return null; }
        })();

        if (draft && draft.content !== content) {
          const useDraft = window.confirm(
            `Unsaved draft found from ${new Date(draft.savedAt).toLocaleString()}.\nUse draft instead of server version?`
          );
          setEditorContent(useDraft ? draft.content : content);
          setEditorDirty(useDraft);
        } else {
          setEditorContent(content);
          setEditorDirty(false);
        }
        setOriginalContent(content);
        setOpenFile(path);
        setEditorTab('edit');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  }, [sendWs, serverId]);

  // Auto-save draft to localStorage
  useEffect(() => {
    if (!openFile || !editorDirty) return;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      if (draftKey) {
        try {
          localStorage.setItem(draftKey, JSON.stringify({ content: editorContent, savedAt: new Date().toISOString() }));
        } catch {}
      }
    }, 1500);
    return () => { if (draftTimer.current) clearTimeout(draftTimer.current); };
  }, [editorContent, editorDirty, openFile, draftKey]);

  const handleEditorChange = (value: string) => {
    setEditorContent(value);
    setEditorDirty(value !== originalContent);
  };

  const saveFile = async () => {
    if (!openFile) return;
    setEditorSaving(true);
    try {
      await serverAPI.saveFile(serverId, openFile, editorContent);
      setOriginalContent(editorContent);
      setEditorDirty(false);
      // Clear draft
      if (draftKey) try { localStorage.removeItem(draftKey); } catch {}
      toast.success('File saved successfully');
      // Reload history
      if (editorTab === 'history') loadFileHistory(openFile);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save file');
    } finally {
      setEditorSaving(false);
    }
  };

  const loadFileHistory = async (path: string) => {
    setHistoryLoading(true);
    try {
      const res = await serverAPI.listFileEdits(serverId, path);
      setFileHistory(res.data?.data?.edits || []);
    } catch {
      setFileHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreEdit = async (editId: number) => {
    if (!window.confirm('Restore this version into the editor? (you can still review before saving)')) return;
    setRestoringEdit(editId);
    try {
      const res = await serverAPI.getFileEditContent(serverId, editId);
      const content = res.data?.data?.edit?.content || '';
      setEditorContent(content);
      setEditorDirty(content !== originalContent);
      setEditorTab('edit');
      toast.success('Version restored to editor — review and save when ready');
    } catch {
      toast.error('Failed to restore version');
    } finally {
      setRestoringEdit(null);
    }
  };

  const closeFile = () => {
    if (editorDirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setOpenFile(null);
    setEditorContent('');
    setOriginalContent('');
    setEditorDirty(false);
    setFileHistory([]);
  };

  const handleEntry = (entry: DirEntry) => {
    if (entry.is_dir) listDirectory(entry.path);
    else openFileInEditor(entry.path);
  };

  const parts = currentPath.split('/').filter(Boolean);
  const breadcrumbs = [
    { label: '/', path: '/' },
    ...parts.map((p, i) => ({ label: p, path: '/' + parts.slice(0, i + 1).join('/') })),
  ];

  // ─── Connection status banner ────────────────────────────────────────────────
  const statusBanner = wsStatus !== 'open' ? (
    <div style={{
      padding: '12px 16px', background: wsStatus === 'connecting' ? '#fef3c7' : '#fee2e2',
      borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
      color: wsStatus === 'connecting' ? '#92400e' : '#991b1b',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>
        {wsStatus === 'connecting'
          ? '⏳ Connecting to agent...'
          : '⚠️ Agent not reachable. Ensure the CloudAyn agent is running on this server and the API WebSocket server is active.'}
      </span>
      {wsStatus === 'closed' && (
        <button
          onClick={() => {
            setWsStatus('connecting');
            const ws = createServerWs(serverId);
            if (!ws) { setWsStatus('closed'); return; }
            wsRef.current = ws;
            ws.onopen = () => { setWsStatus('open'); listDirectory(currentPath); };
            ws.onclose = () => setWsStatus('closed');
            ws.onerror = () => setWsStatus('closed');
            ws.onmessage = (e) => {
              try {
                const msg = JSON.parse(e.data);
                if (msg.requestId && pending.current.has(msg.requestId)) {
                  const cb = pending.current.get(msg.requestId)!;
                  pending.current.delete(msg.requestId);
                  cb(msg.payload ?? msg);
                }
              } catch {}
            };
          }}
          style={{ padding: '4px 12px', background: '#991b1b', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap', marginLeft: '12px' }}
        >
          Retry
        </button>
      )}
    </div>
  ) : null;

  const content = (
    <div style={{ padding: standalone ? '24px' : '0 0 24px 0', maxWidth: '1400px' }}>
      {standalone && <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: 700 }}>File Manager</h2>}

      {statusBanner}

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '16px', background: '#f9fafb', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', flexWrap: 'wrap' }}>
        {breadcrumbs.map((b, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {i > 0 && <span style={{ color: '#9ca3af' }}>/</span>}
            <button
              onClick={() => { setOpenFile(null); listDirectory(b.path); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', fontWeight: i === breadcrumbs.length - 1 ? 600 : 400, padding: '2px 4px', borderRadius: '4px' }}
            >
              {b.label}
            </button>
          </span>
        ))}
        {openFile && (
          <>
            <span style={{ color: '#9ca3af' }}>/</span>
            <span style={{ color: '#111827', fontWeight: 600, padding: '2px 4px' }}>
              {openFile.split('/').pop()}
            </span>
          </>
        )}
      </div>

      {/* Main layout: sidebar always visible when file is open */}
      <div style={{ display: 'flex', gap: '0', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden', background: '#fff', minHeight: '500px' }}>
        {/* ── Directory sidebar ─────────────────────────────── */}
        <div style={{
          width: openFile ? '220px' : '100%',
          borderRight: openFile ? '1px solid #e5e7eb' : 'none',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s',
        }}>
          {loading && <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>Loading...</div>}
          {error && (
            <div style={{ padding: '12px 16px', color: '#ef4444', fontSize: '12px', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
              <i className="bi bi-exclamation-triangle me-1"></i>{error}
            </div>
          )}
          {!loading && (
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {currentPath !== '/' && (
                <div
                  onClick={() => listDirectory(currentPath.split('/').slice(0, -1).join('/') || '/')}
                  style={{ padding: openFile ? '6px 12px' : '8px 16px', cursor: 'pointer', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <i className="bi bi-arrow-up-left" style={{ fontSize: '11px' }}></i>
                  ..
                </div>
              )}
              {entries.map((entry) => (
                <div
                  key={entry.path}
                  onClick={() => handleEntry(entry)}
                  style={{
                    padding: openFile ? '5px 12px' : '8px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    background: openFile === entry.path ? '#ede9fe' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: openFile ? '12px' : '13px',
                  }}
                  onMouseEnter={e => { if (openFile !== entry.path) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = openFile === entry.path ? '#ede9fe' : 'transparent'; }}
                >
                  <span style={{ flexShrink: 0 }}>{entry.is_dir ? '📁' : '📄'}</span>
                  <span style={{ color: entry.is_dir ? '#4f46e5' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{entry.name}</span>
                  {!openFile && !entry.is_dir && (
                    <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{formatSize(entry.size)}</span>
                  )}
                </div>
              ))}
              {entries.length === 0 && !loading && !error && (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>Empty directory</div>
              )}
            </div>
          )}
        </div>

        {/* ── Editor panel ──────────────────────────────────── */}
        {openFile && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Editor toolbar */}
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '10px', background: '#f9fafb', flexShrink: 0 }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {openFile}
                {editorDirty && <span style={{ color: '#f59e0b', marginLeft: '6px' }}>● unsaved</span>}
              </span>
              {/* Sub-tabs */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {(['edit', 'history'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => {
                      setEditorTab(tab);
                      if (tab === 'history' && fileHistory.length === 0) loadFileHistory(openFile);
                    }}
                    style={{ padding: '4px 12px', fontSize: '12px', border: 'none', borderRadius: '5px', cursor: 'pointer', background: editorTab === tab ? '#6366f1' : '#e5e7eb', color: editorTab === tab ? '#fff' : '#374151' }}
                  >
                    {tab === 'edit' ? 'Editor' : 'History'}
                  </button>
                ))}
              </div>
              <button
                onClick={saveFile}
                disabled={editorSaving || !editorDirty || wsStatus !== 'open'}
                style={{ padding: '5px 14px', fontSize: '12px', background: editorDirty && wsStatus === 'open' ? '#6366f1' : '#e5e7eb', color: editorDirty && wsStatus === 'open' ? '#fff' : '#9ca3af', border: 'none', borderRadius: '6px', cursor: editorDirty ? 'pointer' : 'default' }}
                title={wsStatus !== 'open' ? 'Agent not connected' : ''}
              >
                {editorSaving ? 'Saving...' : '💾 Save'}
              </button>
              <button
                onClick={closeFile}
                style={{ padding: '4px 10px', background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', color: '#6b7280' }}
                title="Close file"
              >
                ×
              </button>
            </div>

            {/* Editor content */}
            {editorTab === 'edit' ? (
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <Editor
                  height="100%"
                  language={getLanguage(openFile ?? '')}
                  value={editorContent}
                  theme="vs-dark"
                  onChange={(val) => handleEditorChange(val ?? '')}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                  }}
                />
              </div>
            ) : (
              /* History panel */
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {historyLoading ? (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>
                    <div className="spinner-border spinner-border-sm me-2 text-primary" />Loading history...
                  </div>
                ) : fileHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px' }}>
                    <i className="bi bi-clock-history display-6 d-block mb-2 opacity-50"></i>
                    No saved versions yet. Save the file to create a history entry.
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                      Last {fileHistory.length} save{fileHistory.length !== 1 ? 's' : ''}. Click "Restore" to load a version into the editor.
                    </p>
                    {fileHistory.map((edit, i) => (
                      <div key={edit.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '8px', background: i === 0 ? '#f0fdf4' : '#fff' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500 }}>
                            {i === 0 && <span className="badge bg-success me-2" style={{ fontSize: '10px' }}>latest</span>}
                            {new Date(edit.created_at).toLocaleString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{formatSize(edit.size_bytes)}</div>
                        </div>
                        <button
                          onClick={() => restoreEdit(edit.id)}
                          disabled={restoringEdit === edit.id}
                          style={{ padding: '4px 12px', fontSize: '12px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                        >
                          {restoringEdit === edit.id ? '...' : 'Restore'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return standalone ? <DashboardLayout>{content}</DashboardLayout> : content;
};

export default FileBrowserPage;
